if (!customElements.get('free-shipping-progress')) {
  class FreeShippingProgress extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        defaultMessage: '.f-cartgoal__default-message',
        successMessage: '.f-cartgoal__success-message',
        bar: '.f-cartgoal__bar',
        fill: '.f-cartgoal__bar span',
        thumb: '.f-cartgoal__icon',
      };

      this.cartUpdateUnsubscriber = undefined;
    }

    connectedCallback() {
      // Read config from data attributes set by Liquid
      this.thresholdCents = parseInt(this.dataset.threshold || '0', 10);
      this.eligibilityMode = this.dataset.eligibilityMode || 'REQUIRES_SHIPPING';
      this.rewardLabel = this.dataset.rewardLabel || 'free shipping';
      this.moneyFormat = this.dataset.moneyFormat || window.FoxThemeSettings?.money_format || '${{amount}}';

      // Pending message template — [amount] is replaced with the formatted money value.
      // Set via data-pending-template by the Liquid snippet (sourced from Theme Settings).
      this.pendingTemplate = this.dataset.pendingTemplate || '[amount] away from free shipping';

      // Parse the eligibility map built by Liquid: "variantId:true|variantId:false|..."
      this.eligibilityMap = this._parseEligibilityMap(this.dataset.eligibilityMap || '');

      // Parse eligible collection IDs for COLLECTIONS mode (numeric IDs, comma-separated)
      this.eligibleCollectionIds = new Set(
        (this.dataset.eligibleCollectionIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );

      // Render from the Liquid-computed initial subtotal
      const initialSubtotal = parseInt(this.dataset.eligibleSubtotal || '0', 10);
      this._render(initialSubtotal);

      // Subscribe to cart updates
      if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (cartState) => {
            this._handleCartUpdate(cartState);
          }
        );
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = undefined;
      }
    }

    // ---------------------------------------------------------------------------
    // Cart update handler
    // ---------------------------------------------------------------------------

    /**
     * Called on every cartUpdate event.
     *
     * The event payload varies by trigger (full cart, single added item, etc.),
     * so we always fetch the authoritative cart state via FoxThemeCartHelpers.getState()
     * rather than trying to infer eligibility from the event payload directly.
     * This mirrors exactly how cart-goal.js handles updates.
     */
    async _handleCartUpdate() {
      if (!window.FoxThemeCartHelpers) return;
      let cart;
      try {
        cart = await window.FoxThemeCartHelpers.getState();
      } catch (e) {
        return;
      }
      if (!cart?.items) return;

      await this._updateEligibilityMap(cart.items);
      const eligible = this._getEligibleSubtotal(cart.items);
      this._render(eligible);

      if (cart.item_count === 0) {
        this.classList.add('hidden');
      } else {
        this.classList.remove('hidden');
      }
    }

    // ---------------------------------------------------------------------------
    // Eligibility helpers
    // ---------------------------------------------------------------------------

    /**
     * Parse the Liquid-built eligibility map string.
     * Format: "variantId:true|variantId:false|..."
     * Returns a Map<string, boolean>.
     */
    _parseEligibilityMap(raw) {
      const map = new Map();
      if (!raw) return map;
      raw.split('|').forEach((pair) => {
        const [id, val] = pair.split(':');
        if (id) map.set(id.trim(), val?.trim() === 'true');
      });
      return map;
    }

    /**
     * Sync the eligibility map with the current cart items.
     * For unknown variants:
     *   - REQUIRES_SHIPPING: use item.requires_shipping from the cart API directly.
     *   - COLLECTIONS: /cart.js and /products/{handle}.js do not expose collection
     *     membership, so we re-render the cart-drawer section server-side via the
     *     Shopify Section Rendering API and read the fresh data-eligibility-map
     *     attribute from the returned HTML. Liquid has full access to
     *     item.product.collections and recomputes the map correctly.
     * Returns a Promise that resolves once all unknown items are resolved.
     */
    async _updateEligibilityMap(items) {
      const unknownItems = items.filter(
        (item) => !this.eligibilityMap.has(String(item.variant_id))
      );

      if (unknownItems.length === 0) return;

      if (this.eligibilityMode === 'REQUIRES_SHIPPING') {
        unknownItems.forEach((item) => {
          this.eligibilityMap.set(String(item.variant_id), !!item.requires_shipping);
        });
        return;
      }

      if (this.eligibilityMode === 'COLLECTIONS') {
        // Re-render the cart-drawer section so Liquid can compute collection
        // membership server-side, then read the fresh eligibility map from the HTML.
        // This is necessary because neither /cart.js nor /products/{handle}.js
        // expose collection membership on line items.
        try {
          const url = `${window.location.pathname}?section_id=cart-drawer`;
          const response = await fetch(url);
          if (!response.ok) return;
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const freshEl = doc.querySelector('free-shipping-progress');
          if (freshEl) {
            // Merge the freshly Liquid-rendered eligibility map into ours
            const freshMap = this._parseEligibilityMap(
              freshEl.dataset.eligibilityMap || ''
            );
            freshMap.forEach((val, key) => this.eligibilityMap.set(key, val));
          }
        } catch (e) {
          // Section render failed — eligibility stays as previously computed
        }
      }
    }

    /**
     * Sum the final_line_price of eligible items.
     */
    _getEligibleSubtotal(items) {
      return items.reduce((subtotal, item) => {
        const key = String(item.variant_id);
        const eligible = this.eligibilityMap.get(key) ?? false;
        if (!eligible) return subtotal;
        const linePrice =
          typeof item.final_line_price === 'number'
            ? item.final_line_price
            : typeof item.line_price === 'number'
            ? item.line_price
            : (item.final_price ?? item.price ?? 0) * (item.quantity ?? 1);
        return subtotal + linePrice;
      }, 0);
    }

    // ---------------------------------------------------------------------------
    // Rendering
    // ---------------------------------------------------------------------------

    _render(eligibleCents) {
      const threshold = this.thresholdCents;
      const remaining = Math.max(threshold - eligibleCents, 0);
      const progress = threshold > 0
        ? Math.min((eligibleCents / threshold) * 100, 100)
        : 0;

      const defaultMsg = this.querySelector(this.selectors.defaultMessage);
      const bar = this.querySelector(this.selectors.bar);

      if (defaultMsg) {
        if (remaining > 0) {
          const formattedAmount = formatMoney(remaining, this.moneyFormat);
          defaultMsg.innerHTML = this.pendingTemplate.replace('[amount]', formattedAmount);
        } else {
          defaultMsg.innerHTML = '';
        }
      }

      // Toggle done state (shows success message via CSS)
      if (remaining <= 0) {
        this.classList.add('f-cartgoal--done');
      } else {
        this.classList.remove('f-cartgoal--done');
      }

      // Drive the CSS custom property used by both fill bar and thumb
      this.style.setProperty('--progress', `${progress}%`);

      if (bar) {
        bar.setAttribute('aria-valuenow', Math.round(progress));
      }
    }
  }

  customElements.define('free-shipping-progress', FreeShippingProgress);
}
